import React, { useState } from 'react';
import { Alert, Box, Divider, Stack, Typography } from '@mui/material';
import type {
  FeatureSwitchMove,
  SetFeatureSwitchRequest,
  SwitchDetail,
  SwitchState,
} from '../../../types/backend';
import { describeState } from './featureSwitchForm';
import FeatureSwitchEditor from './FeatureSwitchEditor';

interface Props {
  detail: SwitchDetail;
  availableGroups: string[];
  canSet: boolean;
  onSet: (request: SetFeatureSwitchRequest) => Promise<FeatureSwitchMove>;
}

const when = (iso: string) => iso.replace('T', ' ').replace(/(\.\d+)?(Z|\+00:00)$/, ' UTC');

const Fact: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Typography variant="body2">
    <Box component="span" sx={{ color: 'text.secondary' }}>{label}: </Box>
    {children}
  </Typography>
);

/** The detail pane for one switch: what it gates, who it is enabled for, its last change, and the editor. */
const FeatureSwitchDetail: React.FC<Props> = ({ detail, availableGroups, canSet, onSet }) => {
  const [confirmed, setConfirmed] = useState<string | null>(null);
  // The generated types drop the backend's `| None` on these three fields.
  const state: SwitchState | null = detail.state ?? null;
  const lastMove: FeatureSwitchMove | null = detail.last_move ?? null;
  const unreadable: string | null = detail.unreadable_reason ?? null;
  const unknownGroups = detail.unknown_groups;

  let lastChange = 'Never changed';
  if (lastMove) {
    lastChange = `${lastMove.actor_email} at ${when(lastMove.at)}, `
      + `${describeState(lastMove.from_state)} → ${describeState(lastMove.to_state)}`
      + (lastMove.note ? `: ${lastMove.note}` : '');
  }

  return (
    <Box data-testid={`feature-switch-${detail.name}`} sx={{ p: 2, maxWidth: 'sm' }}>
      <Typography variant="h6">{detail.name}</Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>{detail.description}</Typography>
      <Stack spacing={0.5}>
        {unreadable && <Fact label="Store">{`Unreadable: ${unreadable}`}</Fact>}
        {state && <Fact label="Enabled for">{describeState(state)}</Fact>}
        <Fact label="Last change">{lastChange}</Fact>
      </Stack>
      {unknownGroups.length > 0 && (
        <Alert severity="warning" sx={{ mt: 1.5 }} data-testid={`feature-switch-${detail.name}-unknown-groups`}>
          {`Groups that no longer exist: ${unknownGroups.join(', ')}. They match nobody; `
            + 'save the switch without them to clear them.'}
        </Alert>
      )}
      {confirmed && <Alert severity="success" sx={{ mt: 1.5 }} onClose={() => setConfirmed(null)}>{confirmed}</Alert>}
      {canSet && state && (
        <>
          <Divider sx={{ my: 2 }} />
          <FeatureSwitchEditor
            key={JSON.stringify(state)}
            name={detail.name}
            current={state}
            availableGroups={availableGroups}
            onSet={onSet}
            onSaved={() => setConfirmed('Saved. It holds everywhere within about 15 seconds.')}
          />
        </>
      )}
    </Box>
  );
};

export default FeatureSwitchDetail;
